import {
  Component, EventEmitter, Input, Output, forwardRef, OnInit, OnDestroy, ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor, NG_VALUE_ACCESSOR, ValidationErrors,
} from '@angular/forms';
import type { Editor } from 'src/app/assets/tinymce/tinymce';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-rich-text-editor',
  template: `
    <div class="rich-text-editor-container" appRichTextEditorValidation>
      <editor
        #editor
        class="rich-text-editor"
        [ngClass]="touched && hasError ? 'error-state' : ''"
        [init]="{
          license_key: 'gpl',
          base_url: '/assets/tinymce',
          suffix: '.min',
          height: 500,
          min_height: 250,
          resize: true,
          default_link_target: '_blank',
          menubar: false,
          plugins: 'autoresize autolink lists link image wordcount',
          toolbar: 'undo redo | blocks | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist | link',
          content_style: '@import url(https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Roboto+Mono:ital,wght@0,100..700;1,100..700&display=swap);body { font-family:Inter,Helvetica,Arial,sans-serif; font-size:16px; } h1, h2, h3, h4, h5, h6 { font-family:Inter,Helvetica,Arial,sans-serif; } pre { font-family:Roboto Mono,monospace; } a { color: #0097A3; cursor: pointer; text-decoration: none; } a:hover { color: #2AA8B2; } a:active { color: #007D87; }'
        }"
        [initialValue]="value || initialValue"
        (onKeyUp)="onEditorKeyUp($event)"
        (onChange)="onEditorContentChange($event)"
        (onBlur)="onEditorBlur()"
        (onFocus)="onEditorFocus()"
        (onInit)="onEditorInit($event)"
      ></editor>
      <div class="error-container">
        <p class="klabel-medium error" *ngIf="touched && hasError">
          {{ getErrorMessage() }}
        </p>
        <p class="klabel-medium debug" *ngIf="debug">
          Debug: touched={{ touched }}, hasError={{ hasError }}, currentErrors={{ currentErrors | json }}
        </p>
      </div>
    </div>
  `,
  styles: [
    `
    .rich-text-editor-container {
      display: flex;
      flex-direction: column;
      width: 100%;
    }
    .rich-text-editor {
      width: 100%;
    }
    .error {
      color: #d32f2f;
      font-size: 12px;
      margin-top: 6px;
    }
    .error-state {
      box-shadow: 0 0 5px 0 #af2f0d;
      border-radius: 5px;
    }
  `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
  ],
})
export class RichTextEditorComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() initialValue = '';
  @Input() debug = false;
  @Input() fieldName = 'This field';
  @Input() required: boolean | string = false;
  @Input() minLength = 0;
  @Input() maxLength?: number;
  @Output() updatedValue = new EventEmitter<string>();

  errorMessage = '';
  @ViewChild('editor', { static: false }) editorRef: any;
  editor: Editor;
  value = '';
  textValue = '';
  touched = false;
  disabled = false;
  hasError = false;
  currentErrors: ValidationErrors | null = null;
  private subscription = new Subscription();

  // ControlValueAccessor methods
  private onChange = (value: string) => {};
  private onTouched = () => {};

  // Getter to handle required input as either boolean or string
  get isRequired(): boolean {
    return this.required === true || this.required === 'required' || this.required === '';
  }

  constructor() {}

  ngOnInit() {
    // Initialize with initial value only if no ngModel value is set
    if (this.initialValue && !this.value) {
      this.value = this.initialValue;
    }
  }

  ngOnDestroy() {
    // Cleanup editor if it exists
    if (this.editor) {
      this.editor.destroy();
    }
    this.subscription.unsubscribe();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value = value || '';
    // Update the editor content if it exists
    if (this.editor) {
      this.editor.setContent(this.value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    // You can add logic here to disable the TinyMCE editor
  }

  // Event handlers
  onEditorKeyUp(event: any) {
    this.touched = true;
    // For onKeyUp, we need to get the editor from the component
    if (this.editor) {
      const htmlContent = this.editor.getContent();
      const textContent = this.editor.getContent({ format: 'text' });

      this.value = htmlContent;
      this.textValue = textContent;
      this.updatedValue.emit(htmlContent);

      // Trigger validation
      this.updateValidationState();
    }
  }

  onEditorContentChange(event: any) {
    const editor = event.event.target;
    const textContent = editor.getContent({ format: 'text' });
    const htmlContent = editor.getContent();

    this.value = htmlContent; // Use text content for validation
    this.textValue = textContent;
    this.onChange(htmlContent);
    this.updatedValue.emit(htmlContent);

    // Manually trigger validation state update
    this.updateValidationState();
  }

  private updateValidationState() {
    // Check if we have validation errors based on current value
    const errors: ValidationErrors = {};

    if (this.isRequired && (!this.textValue || this.textValue.trim() === '')) {
      errors['required'] = true;
    }

    if (this.minLength > 0 && this.textValue.length < this.minLength) {
      errors['minlength'] = { requiredLength: this.minLength, actualLength: this.textValue.length };
    }

    if (this.maxLength && this.textValue.length > this.maxLength) {
      errors['maxlength'] = { requiredLength: this.maxLength, actualLength: this.textValue.length };
    }

    this.currentErrors = Object.keys(errors).length > 0 ? errors : null;
    this.hasError = this.touched && Object.keys(errors).length > 0;
  }

  onEditorBlur() {
    this.touched = true;
    this.onTouched();

    // Update validation state after setting touched
    this.updateValidationState();
  }

  onEditorFocus() {
    // Handle focus if needed
  }

  onEditorInit(event: any) {
    this.editor = event.editor;
    // Set content if we have a value
    if (this.value) {
      this.editor.setContent(this.value);
    }
  }

  getErrorMessage(): string {
    if (!this.currentErrors) {
      return '';
    }

    if (this.currentErrors['required']) {
      return `${this.fieldName} is required`;
    }

    if (this.currentErrors['minlength']) {
      const error = this.currentErrors['minlength'];
      return `${this.fieldName} must be at least ${error.requiredLength} characters (currently ${error.actualLength})`;
    }

    if (this.currentErrors['maxlength']) {
      const error = this.currentErrors['maxlength'];
      return `${this.fieldName} must be no more than ${error.requiredLength} characters (currently ${error.actualLength})`;
    }

    return '';
  }
}
